import { Button, Image } from '@mantine/core';
import { modals } from '@mantine/modals';
import type { IssueType } from '@prisma/client';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMemo } from 'react';
import { api } from '~/trpc-extra/api';
import { useProjectId } from '~/hooks/context/useProjectId';
import { LinearCreateIssueView } from '../linear/LinearCreateIssueView';
import IntegrationStatusDialog from '../integrations/IntegrationStatusDialog';
import TestRunAttemptIssueDialog from './TestRunAttemptIssueDialog';

const getIssueIcon = (issueType?: IssueType) =>
  issueType === 'BUG' ?
    <IconAlertCircle size={16} />
    // No icon for other issue types
  : null;

type Issue = {
  id: string;
  issueType: string;
  status: string;
};

type TestRunAttemptIssueSnippetProps = {
  attemptId: string;
  runId: string;
  issues?: Issue[];
  hasLinearInstalled?: boolean;
  testName?: string;
  errorMessage?: string;
};

export default function TestRunAttemptIssueSnippet({
  attemptId,
  runId,
  issues: issuesProp,
  hasLinearInstalled: hasLinearInstalledProp,
  testName: testNameProp,
  errorMessage: errorMessageProp,
}: TestRunAttemptIssueSnippetProps) {
  const projectId = useProjectId();

  // Only query if props not provided (for backward compatibility with header usage)
  const { data: queriedIssues = [] } = api.testRun.getIssues.useQuery(
    { id: { attemptId } },
    { enabled: issuesProp === undefined },
  );
  const { data: queriedHasLinearInstalled } = api.project.hasLinearInstalled.useQuery(
    { projectId: projectId || '' },
    { enabled: hasLinearInstalledProp === undefined },
  );
  const { data: queriedTestRun } = api.testRun.get.useQuery({ runId }, { enabled: testNameProp === undefined });

  // Use props if provided, otherwise use queried data
  const issues = issuesProp ?? queriedIssues;
  const hasLinearInstalled = hasLinearInstalledProp ?? queriedHasLinearInstalled ?? false;
  const testName = testNameProp ?? queriedTestRun?.testV2?.name;
  const errorMessage =
    errorMessageProp ?? queriedTestRun?.TestRunAttempts.find((a) => a.id === attemptId)?.AttemptError?.message;

  const allIssuesResolved = useMemo(() => issues.every((issue) => issue.status === 'RESOLVED'), [issues]);

  const hasBugIssue = useMemo(() => issues.some((issue) => issue.issueType === 'BUG'), [issues]);

  const getPrefilledDetails = () => {
    const name = testName || 'Test';
    const error = errorMessage;

    return {
      title: `Test Failed: ${name}`,
      description:
        error ?
          `Test "${name}" failed with the following error:\n\n\`\`\`\n${error}\n\`\`\``
        : `Test "${name}" failed during execution.`,
    };
  };

  const handleAddIssue = () => {
    if (hasLinearInstalled) {
      modals.open({
        title: (
          <div className="flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded bg-gray-900">
              <Image
                src="/assets/linear-light-logo.svg"
                alt="Linear logo"
                className="h-4 w-4"
              />
            </div>
            <div className="font-semibold">Create Linear Issue</div>
          </div>
        ),
        size: 'lg',
        zIndex: 310,
        children: (
          <LinearCreateIssueView
            projectId={projectId || ''}
            prefilledDetails={getPrefilledDetails()}
          />
        ),
      });
    } else {
      modals.open({
        title: 'Connect to Issue Tracker',
        size: 'md',
        zIndex: 310,
        children: <IntegrationStatusDialog />,
      });
    }
  };

  return issues.length === 0 ?
      <Button
        variant="default"
        onClick={handleAddIssue}
      >
        + Issue
      </Button>
    : <Button
        variant="outline"
        color={
          allIssuesResolved ? 'green'
          : hasBugIssue ?
            'red'
          : undefined
        }
        onClick={() => {
          modals.open({
            title: 'Issues',
            size: 'xl',
            zIndex: 310,
            children: (
              <TestRunAttemptIssueDialog
                runId={runId}
                attemptId={attemptId}
              />
            ),
          });
        }}
      >
        <div className="flex items-center space-x-1">
          <span>
            {allIssuesResolved ?
              `✓ Resolved`
            : issues.length === 1 && issues[0].issueType === 'BUG' ?
              '1 Bug'
            : `${issues.length} Issue${issues.length === 1 ? '' : 's'}`}
          </span>
          {getIssueIcon(issues.at(0)?.issueType === 'BUG' ? 'BUG' : undefined)}
        </div>
      </Button>;
}
